// Firm status page (Status artboard): stage cards, every step, right rail.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Download, FileText, PenLine, Upload, X } from 'lucide-react';
import {
  REVIEW_STEP, slotDef, type FirmAgreementView, type IntakeFileView, type KickoffView, type MessageView, type StatusPage as StatusData, type StatusStepView,
} from '@upm/domain';
import { api, ApiError, uploadWithProgress } from '../lib/api';
import { firstName, fmtCalendarDate, fmtDate, fmtFullDay, fmtMonthDay, fmtShortDay, fmtTime, fmtTimeRange, zoneShort } from '../lib/format';
import { Avatar, Banner, Button, ButtonA, ButtonLink, Card, Dialog, Pill, ProgressBar, Spinner, TextArea, cx } from '../ui';
import { FirmHeader, FirmPage } from '../firm/FirmHeader';
import { FirmMessage } from '../firm/Message';
import { NoSession } from '../firm/Entry';
import { ACCEPT, FileSlots, PLANNING_COPIES, PLANNING_HOLD_OFF, uploadError } from '../intake/FileSlots';
import { KickoffPicker } from '../intake/KickoffPicker';
import { firmDocUrl } from '../agreement/SignAgreement';
import { usePageHeading } from '../firm/usePageHeading';

const STAGE_PILL: Record<StatusData['stages'][number]['state'], string> = { done: 'Done', in_progress: 'In progress', up_next: 'Up next', later: 'Later' };

export function StatusPage() {
  const [data, setData] = useState<StatusData | null>(null);
  const [state, setState] = useState<'loading' | 'nosession' | 'error' | 'ok'>('loading');

  const load = useCallback(async () => {
    try { setData(await api.get<StatusData>('/api/intake/status')); setState('ok'); }
    catch (e) { setState(e instanceof ApiError && e.status === 401 ? 'nosession' : 'error'); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  usePageHeading(data ? 'Your migration status' : null);

  // Poll while a file is being scanned or the signed agreement is being prepared.
  const pending = !!data?.filesShared.some((f) => f.scan === 'pending') || data?.agreement?.signedCopy === 'preparing';
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => { void load(); }, 5000);
    return () => clearInterval(t);
  }, [pending, load]);

  if (state === 'nosession') return <NoSession />;
  if (state === 'error') return <FirmMessage title="Something went wrong"><p className="m-0">We couldn’t load your migration status. Refresh the page to try again.</p></FirmMessage>;
  if (!data) return <div className="min-h-screen bg-page"><Spinner label="Loading your migration status" /></div>;

  return <StatusView data={data} onReload={load} onMessage={(m) => setData((d) => (d ? { ...d, messages: [...d.messages, m] } : d))} />;
}

/** The status page itself, from loaded data. Staff render it read-only as the firm preview (admin/FirmPreview). */
export function StatusView({ data, onReload, onMessage }: { data: StatusData; onReload: () => Promise<void>; onMessage: (m: MessageView) => void }) {
  const healthOk = data.health.state === 'on_track';
  return (
    <FirmPage>
      <FirmHeader subtitle={data.firmName} lead={data.lead} onMessageSent={onMessage} />
      <main className="mx-auto flex max-w-shell flex-col gap-6 px-4 pb-16 pt-6 sm:px-8 sm:pt-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col gap-2">
            <span className="eyebrow">Migration status</span>
            <h1 className="m-0 text-[26px] font-bold leading-[1.2] tracking-[-0.02em] sm:text-[30px]" tabIndex={-1} data-page-heading style={{ outline: "none" }}>Your move to Upstream Portal</h1>
          </div>
          <Pill tone={healthOk ? 'ok' : 'warn'} dot className="!px-3 !py-1.5 !text-sm">
            {healthOk ? 'On track' : 'Needs attention'} · Updated {fmtMonthDay(data.health.updatedAt)}
          </Pill>
        </div>

        <ol aria-label="Migration stages" className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3 p-0">
          {data.stages.map((st, i) => {
            const active = st.state === 'in_progress';
            return (
              <li key={st.id} aria-current={active ? 'step' : undefined} className={cx('flex flex-col rounded-card bg-white px-5 py-[18px]', active ? 'border-[1.5px] border-brand' : 'border border-line')}>
                <div className="flex items-center justify-between gap-2">
                  <span className={cx('text-xs font-bold uppercase tracking-[0.06em]', active ? 'text-brand' : 'text-muted')}>{i + 1} · {st.title}</span>
                  <Pill tone={active ? 'brand' : st.state === 'done' ? 'ok' : 'neutral'} className="!py-0 !text-xs">{STAGE_PILL[st.state]}</Pill>
                </div>
                {/* Phones show the current stage in full and the others as a title and progress. */}
                <div className={cx('mb-3.5 mt-2 flex-1 flex-col gap-2 text-sm', active ? 'flex' : 'hidden sm:flex')}>
                  <p className="m-0 text-ink-2">{st.blurb}</p>
                  {/* Pinned to the bottom so the four outcomes line up across the row. */}
                  <p className="m-0 mt-auto font-semibold text-ink">Outcome: {st.outcome}</p>
                </div>
                <ProgressBar pct={st.total ? (st.done / st.total) * 100 : 0} />
                <div className="mt-1.5 text-xs text-muted">{st.done} of {st.total} done</div>
              </li>
            );
          })}
        </ol>

        <div className="flex flex-wrap items-start gap-6">
          <StepsList data={data} />
          {/* Phones: what the firm does next comes before the full list of steps. */}
          <aside className="order-first flex min-w-0 flex-[1_1_300px] flex-col gap-4 md:order-none">
            {/* The firm's turn to sign, while the next-step card is about the Discovery Session. */}
            {data.agreement?.status === 'sent' && data.nextStep?.kind !== 'agreement' && <AgreementCard agreement={data.agreement} eyebrow="Waiting on you" />}
            <NextStep data={data} onChange={onReload} />
            {data.tasks.length > 0 && <YourTasks data={data} />}
            {/* Upload slots only once staff request files (Review → Request files). */}
            {data.filesRequested && <WhatWeNeed data={data} onChange={onReload} />}
            <DocumentsCard data={data} onChange={onReload} />
            <Decisions data={data} />
            <Messages data={data} onSent={onMessage} />
          </aside>
        </div>
      </main>
    </FirmPage>
  );
}

function stepDetail(s: StatusStepView, data: StatusData) {
  if (s.id === 'plan.intake' && s.state === 'done') {
    return <>Submitted {s.at ? fmtMonthDay(s.at) : ''} · <Link to={`/onboard?step=${REVIEW_STEP}`}>View or update your answers</Link></>;
  }
  if (s.id === 'plan.agreement' && data.agreement) return <AgreementStepDetail a={data.agreement} lead={data.lead.name} />;
  if (s.id === 'plan.kickoff_booked') {
    const k = data.nextStep?.kind === 'kickoff' ? data.nextStep.kickoff : null;
    const at = s.at ?? k?.startsAt;
    if (at && (s.state === 'done' || k?.status === 'booked')) return `${fmtFullDay(at)} at ${fmtTime(at)} ${zoneShort(new Date(at))}`;
  }
  return s.detail;
}

function StepsList({ data }: { data: StatusData }) {
  return (
    <section aria-labelledby="steps-h" className="flex min-w-0 flex-[999_1_560px] flex-col gap-4 rounded-2xl border border-line bg-white p-5 sm:p-7">
      <h2 id="steps-h" className="m-0 text-xl font-bold tracking-[-0.01em]">Every step, start to finish</h2>
      {data.stages.map((st) => {
        const steps = data.steps.filter((s) => s.stage === st.id);
        const detailed = st.state === 'in_progress' || st.state === 'done' || steps.some((s) => s.state === 'done');
        return (
          <div key={st.id}>
            <h3 className={cx('mt-0 text-[13px] font-bold uppercase tracking-[0.06em]', detailed ? 'mb-1.5 text-brand' : 'mb-0.5 text-muted')}>{st.title}</h3>
            {detailed ? (
              <ul className="m-0 flex list-none flex-col p-0">
                {steps.map((s) => {
                  const detail = stepDetail(s, data);
                  return (
                    <li key={s.id} className={cx('flex gap-3 rounded-[10px] px-2.5 py-3', s.state === 'next' && 'bg-brand-tint')}>
                      {s.state === 'done' ? (
                        <span aria-hidden="true" className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-brand"><Check size={14} strokeWidth={3} className="text-white" /></span>
                      ) : (
                        <span aria-hidden="true" className={cx('box-border h-6 w-6 flex-none rounded-full', s.state === 'next' ? 'border-2 border-brand' : 'border-[1.5px] border-line-btn')} />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold">{s.label}</div>
                        {detail && <div className="text-sm text-muted">{detail}</div>}
                      </div>
                      {s.state === 'done' && <span className="text-[13px] font-semibold text-muted">Done</span>}
                      {s.state === 'next' && <span className="text-[13px] font-semibold text-brand">Next</span>}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <details className="group text-sm text-ink-2">
                <summary className="cursor-pointer list-none text-muted hover:text-ink">
                  {steps.length} steps · <span className="font-semibold text-brand group-open:hidden">Show</span><span className="hidden font-semibold text-brand group-open:inline">Hide</span>
                </summary>
                <ul className="m-0 mt-1.5 flex list-disc flex-col gap-1.5 pl-5 marker:text-muted">
                  {steps.map((s) => <li key={s.id}>{s.label}</li>)}
                </ul>
              </details>
            )}
          </div>
        );
      })}
    </section>
  );
}

function NextStep({ data, onChange }: { data: StatusData; onChange: () => Promise<void> }) {
  const [mode, setMode] = useState<'view' | 'reschedule' | 'book'>('view');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lead = firstName(data.lead.name, 'us');
  const n = data.nextStep;
  if (!n) return null;

  const picker = (verb: string, submit: (startsAt: string) => Promise<KickoffView>, contact?: () => Promise<void>) => (
    <div className="mt-4">
      <KickoffPicker leadName={data.lead.name} confirmVerb={verb} onConfirm={async (s) => { const k = await submit(s); setMode('view'); await onChange(); return k; }} onContactMe={contact} />
      <Button variant="link" size="sm" className="mt-2" onClick={() => setMode('view')}>Keep the current plan</Button>
    </div>
  );

  if (n.kind === 'agreement') return <AgreementCard agreement={n.agreement} eyebrow="Your next step" />;
  if (n.kind === 'intake') {
    return (
      <Card role="region" aria-label="Your next step">
        <div className="eyebrow !text-xs">Your next step</div>
        <div className="mt-1 text-[17px] font-semibold">Finish your questionnaire</div>
        <div className="text-sm text-ink-2">Your answers are saved. Pick up where you left off, then book your Discovery Session with {firstName(data.lead.name)}.</div>
        <div className="mt-3.5"><ButtonLink to="/onboard" variant="primary" size="md" className="!px-[18px]">Continue your questionnaire</ButtonLink></div>
      </Card>
    );
  }
  if (n.kind === 'text') {
    return (
      <Card>
        <div className="eyebrow !text-xs">Your next step</div>
        <div className="mt-1 text-sm text-ink-2">{n.text}</div>
      </Card>
    );
  }
  const k = n.kind === 'kickoff' ? n.kickoff : null; // null: submitted, nothing booked yet
  const booked = k?.status === 'booked' && k.startsAt;
  return (
    <Card role="region" aria-label="Your next step">
      <div className="eyebrow !text-xs">Your next step</div>
      <div className="mt-1 text-[17px] font-semibold">Discovery Session</div>
      {booked && k?.startsAt && (
        <div className="text-sm text-ink-2">{fmtShortDay(k.startsAt)} · {fmtTimeRange(k.startsAt, k.endsAt)} {zoneShort(new Date(k.startsAt))} · Video call with {firstName(data.lead.name)}</div>
      )}
      {!k && <div className="text-sm text-ink-2">A 45-minute video call with {firstName(data.lead.name)} to go through your answers and walk you through how the move works.</div>}
      {k?.status === 'contact_requested' && <div className="text-sm text-ink-2">{lead} will email you to find a time that works.</div>}
      {k?.status === 'cancelled' && <div className="text-sm text-ink-2">Your Discovery Session was cancelled. Pick a new time when you’re ready.</div>}
      {k?.status === 'held' && <div className="text-sm text-ink-2">Held{k.startsAt ? ` ${fmtDate(k.startsAt)}` : ''}. Thank you.</div>}
      {error && <Banner tone="error" role="alert" className="mt-3">{error}</Banner>}
      {mode === 'view' && (
        <div className="mt-3.5 flex flex-wrap items-center gap-2.5">
          {booked && k?.joinUrl && <ButtonA href={k.joinUrl} target="_blank" rel="noreferrer" variant="primary" size="md" className="!px-[18px]">Join call</ButtonA>}
          {booked && <Button variant="link" onClick={() => setMode('reschedule')}>Reschedule</Button>}
          {booked && <Button variant="quiet" onClick={() => setConfirmCancel(true)}>Cancel</Button>}
          {!k && <Button variant="primary" onClick={() => setMode('book')}>Pick a time</Button>}
          {(k?.status === 'cancelled' || k?.status === 'contact_requested') && <Button variant="outline" onClick={() => setMode('book')}>Pick a time</Button>}
        </div>
      )}
      {mode === 'reschedule' && picker('Move to', (startsAt) => api.patch<KickoffView>('/api/intake/kickoff', { startsAt }))}
      {mode === 'book' && picker('Confirm', (startsAt) => api.post<KickoffView>('/api/intake/kickoff', { startsAt }),
        k?.status === 'contact_requested' ? undefined : async () => {
          // Refused because a session is already booked (an old page): show the booking with the reason on this card,
          // since the picker (and its own message) closes.
          try { await api.post('/api/intake/kickoff', { contactMe: true }); }
          catch (e) { if (!(e instanceof ApiError && e.code === 'invalid_transition')) throw e; setError(e.message); }
          setMode('view'); await onChange();
        })}
      <Dialog
        open={confirmCancel}
        title="Cancel your Discovery Session?"
        onClose={() => setConfirmCancel(false)}
        footer={<>
          <Button onClick={() => setConfirmCancel(false)}>Keep it</Button>
          <Button variant="danger" onClick={async () => {
            setConfirmCancel(false);
            try { await api.patch('/api/intake/kickoff', { cancel: true }); await onChange(); }
            catch { setError('We couldn’t cancel the call. Try again.'); }
          }}>Cancel the call</Button>
        </>}
      >
        <p className="m-0 text-ink-2">{lead} gets a cancellation in Outlook. You can pick a new time here anytime.</p>
      </Dialog>
    </Card>
  );
}

/** "Sign the agreement" step: what's happening with the agreement, and the action when it's the firm's turn. */
function AgreementStepDetail({ a, lead }: { a: FirmAgreementView; lead: string }) {
  switch (a.status) {
    case 'sent':
      return (
        <span className="mt-1 flex flex-col items-start gap-2">
          <span>Your agreement is ready to review and sign.</span>
          <ButtonLink to="/onboard/agreement" variant="primary" size="sm"><PenLine aria-hidden="true" size={16} />Review and sign</ButtonLink>
        </span>
      );
    case 'firm_signed':
      return <>You signed {a.signer.signedAt ? fmtMonthDay(a.signer.signedAt) : ''}. Waiting for Vertekal to countersign.</>;
    case 'executed':
      return (
        <span className="flex flex-wrap items-center gap-x-2">
          <span>Signed{a.executedAt ? ` ${fmtMonthDay(a.executedAt)}` : ''}.</span>
          {a.signedCopy === 'ready'
            ? <a href={firmDocUrl(a.id, 'final')} download className="inline-flex items-center gap-1 font-semibold"><Download aria-hidden="true" size={14} />Download the signed agreement</a>
            : <span>Preparing your signed copy…</span>}
        </span>
      );
    case 'declined':
      return <>You declined it{a.signer.declinedAt ? ` on ${fmtMonthDay(a.signer.declinedAt)}` : ''}. {firstName(lead)} will be in touch.</>;
  }
}

function AgreementCard({ agreement, eyebrow }: { agreement: FirmAgreementView; eyebrow: string }) {
  return (
    <Card role="region" aria-label="Sign the agreement">
      <div className="eyebrow !text-xs">{eyebrow}</div>
      <div className="mt-1 text-[17px] font-semibold">Sign the agreement</div>
      <div className="text-sm text-ink-2 [overflow-wrap:anywhere]">{agreement.fileName}. Read it, then type your name to sign. {agreement.countersigner.name} countersigns for Vertekal.</div>
      <div className="mt-3.5"><ButtonLink to="/onboard/agreement" variant="primary" size="md"><PenLine aria-hidden="true" size={18} />Review and sign</ButtonLink></div>
    </Card>
  );
}

function WhatWeNeed({ data, onChange }: { data: StatusData; onChange: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState<StatusData['missingFiles']>([]);
  const missing = data.missingFiles;
  const filesFor = (id: string) => data.filesShared.filter((f) => f.slotId === id);
  const slots = [...snapshot.map((m) => ({ ...slotDef(m.slotId), title: m.title, exportGuideUrl: m.exportGuideUrl, files: filesFor(m.slotId) })),
    ...(snapshot.some((m) => m.slotId === 'anything_else') ? [] : [{ ...slotDef('anything_else'), files: filesFor('anything_else') }])];
  return (
    <Card>
      <h2 className="card-title">What we need from you</h2>
      <div className="mt-1 text-sm text-ink-2">
        {missing.length > 0
          ? `${missing.length === 1 ? '1 migration file' : `${missing.length} migration files`} still to share. Upload what you can, and we'll help with the rest.`
          : 'Nothing right now. Thank you for sharing your files.'}
      </div>
      {missing.length > 0 && (
        <ul className="m-0 mt-2 list-disc pl-5 text-sm text-ink-2 marker:text-muted">{missing.map((m) => <li key={m.slotId}>{m.title}</li>)}</ul>
      )}
      <Button variant="link" size="sm" className="mt-2.5 !text-sm" aria-expanded={open} onClick={() => { if (!open) setSnapshot(missing); setOpen((o) => !o); }}>
        {open ? 'Done sharing files' : 'Share files'}
      </Button>
      {open && (
        <div className="mt-3 flex flex-col gap-3">
          <Banner tone="lock">{PLANNING_COPIES}</Banner>
          <FileSlots slots={slots} onUploaded={() => void onChange()} onRemoved={() => void onChange()} dropAnywhere />
        </div>
      )}
    </Card>
  );
}

/**
 * Documents (design §15), always on the page and compact: Upload in the header (to the anything_else slot, open at
 * any time), then "From us" (what its team shared, New until first opened) and "From you" (everything it sent),
 * three of each until Show all. Migration-file slot files can be removed only while uploads are open there.
 */
function DocumentsCard({ data, onChange }: { data: StatusData; onChange: () => Promise<void> }) {
  const [err, setErr] = useState<string | null>(null);
  const [uploads, setUploads] = useState<Array<{ key: string; name: string; progress: number; error: string | null }>>([]);
  const input = useRef<HTMLInputElement>(null);
  const upload = (files: FileList) => {
    for (const file of Array.from(files)) {
      const key = `${file.name}:${Math.random().toString(36).slice(2)}`;
      setUploads((u) => [...u, { key, name: file.name, progress: 0, error: null }]);
      const form = new FormData();
      form.append('slotId', 'anything_else');
      form.append('file', file, file.name);
      uploadWithProgress<IntakeFileView>('/api/intake/files', form, (p) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: p } : x)))).promise.then(
        () => { setUploads((u) => u.filter((x) => x.key !== key)); void onChange(); },
        (e) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: uploadError(e) } : x))),
      );
    }
  };
  const remove = async (f: IntakeFileView) => {
    setErr(null);
    try { await api.del(`/api/intake/files/${f.id}`); await onChange(); }
    catch (e) { setErr(e instanceof ApiError && e.code !== 'internal' ? e.message : `We couldn’t remove ${f.fileName}. Try again.`); }
  };
  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <h2 className="card-title m-0">Documents</h2>
        <input ref={input} type="file" multiple accept={ACCEPT} className="sr-only" tabIndex={-1} aria-hidden="true"
          onChange={(e) => { if (e.target.files?.length) upload(e.target.files); e.target.value = ''; }} />
        <Button variant="outline" size="sm" onClick={() => input.current?.click()}><Upload aria-hidden="true" size={15} />Upload</Button>
      </div>
      <p className="mb-0 mt-1 text-[12.5px] leading-snug text-muted">{PLANNING_HOLD_OFF}</p>
      {uploads.map((u) => (
        <div key={u.key} className="mt-2 text-[13px]" role={u.error ? 'alert' : undefined}>
          <span className={cx('[overflow-wrap:anywhere]', u.error ? 'font-semibold text-bad' : 'text-ink-2')}>{u.error ? `${u.name}: ${u.error}` : `Uploading ${u.name} · ${Math.round(u.progress * 100)}%`}</span>
          {u.error && <Button variant="quiet" size="sm" onClick={() => setUploads((x) => x.filter((y) => y.key !== u.key))}>Dismiss</Button>}
        </div>
      ))}
      {err && <div role="alert" className="mt-2 text-sm font-semibold text-bad">{err}</div>}
      <DocList title="From us" items={data.documents.map((d) => ({
        key: d.id, name: d.fileName, href: `/api/intake/documents/${d.id}`, onOpen: () => setTimeout(() => void onChange(), 1500),
        badge: d.new ? 'New' : null, right: fmtMonthDay(d.at),
      }))} />
      <DocList title="From you" items={data.filesShared.map((f) => ({
        key: f.id, name: f.fileName, tone: f.scan === 'rejected' ? 'bad' as const : undefined,
        right: f.scan === 'clean' ? fmtMonthDay(f.uploadedAt) : f.scan === 'pending' ? 'Checking…' : 'Not accepted',
        onRemove: f.slotId === 'anything_else' || data.filesRequested ? () => void remove(f) : undefined,
      }))} />
    </Card>
  );
}

type DocItem = { key: string; name: string; href?: string; onOpen?: () => void; badge?: string | null; right: string; tone?: 'bad'; onRemove?: () => void };

/** One list on the Documents card: three rows until Show all. */
function DocList({ title, items }: { title: string; items: DocItem[] }) {
  const [all, setAll] = useState(false);
  if (!items.length) return null;
  const shown = all ? items : items.slice(0, 3);
  return (
    <>
      <h3 className="mb-0.5 mt-3 text-xs font-bold uppercase tracking-[0.06em] text-muted">{title}</h3>
      <ul className="m-0 flex list-none flex-col p-0" aria-label={`Documents ${title.toLowerCase()}`}>
        {shown.map((d) => (
          <li key={d.key} className="flex items-center gap-2 border-b border-line-soft py-1.5 text-sm last:border-b-0">
            <FileText aria-hidden="true" size={16} className={cx('flex-none', d.tone === 'bad' ? 'text-bad' : 'text-ok')} />
            {d.href
              ? <a href={d.href} target="_blank" rel="noopener" onClick={d.onOpen} className="line-clamp-2 min-w-0 font-semibold [overflow-wrap:anywhere]" title={d.name}>{d.name}</a>
              : <span className="line-clamp-2 min-w-0 [overflow-wrap:anywhere]" title={d.name}>{d.name}</span>}
            {d.badge && <Pill tone="info" className="!px-1.5 !py-0 !text-[11px]">{d.badge}</Pill>}
            <span className={cx('ml-auto flex-none text-[12.5px]', d.tone === 'bad' ? 'font-semibold text-bad' : 'text-muted')}>{d.right}</span>
            {d.onRemove && (
              <button type="button" aria-label={`Remove ${d.name}`} title="Remove" onClick={d.onRemove}
                className="flex h-7 w-7 flex-none items-center justify-center rounded-md border-0 bg-transparent text-muted hover:bg-panel hover:text-ink">
                <X aria-hidden="true" size={14} />
              </button>
            )}
          </li>
        ))}
      </ul>
      {items.length > 3 && <Button variant="link" size="sm" className="self-start" onClick={() => setAll((a) => !a)}>{all ? 'Show fewer' : `Show all ${items.length}`}</Button>}
    </>
  );
}

/** Tasks staff gave the firm in the open gate (Review → Gates). Read-only here: the lead ticks them off. */
function YourTasks({ data }: { data: StatusData }) {
  return (
    <Card>
      <h2 className="eyebrow m-0 !text-xs">Your to-dos</h2>
      <ul className="m-0 mt-1.5 flex list-none flex-col gap-1.5 p-0" title={`${firstName(data.lead.name)} ticks each one off when it’s done.`}>
        {data.tasks.map((t) => (
          <li key={t.id} className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-ink">{t.title}</span>
            {t.dueDate && <span className="flex-none text-[12.5px] text-muted">Due {fmtMonthDay(`${t.dueDate}T12:00:00`)}</span>}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Decisions({ data }: { data: StatusData }) {
  const [all, setAll] = useState(false);
  if (!data.decisions.length) return null;
  const latest = [...data.decisions].reverse();
  const shown = all ? latest : latest.slice(0, 2);
  return (
    <Card>
      <h2 className="card-title">Decisions we’ve made together</h2>
      <ul className="m-0 mt-2 flex list-none flex-col gap-2.5 p-0">
        {shown.map((d) => (
          <li key={d.id} className="text-sm">
            <div className="text-ink">{d.summary}</div>
            {/* A decision is a calendar date (stored at midnight UTC): shown as that date in every time zone. */}
            <div className="text-[12.5px] text-muted">{fmtCalendarDate(d.decidedAt)} · Approved by {d.approvedBy}</div>
          </li>
        ))}
      </ul>
      {latest.length > 2 && <Button variant="link" size="sm" className="mt-1" onClick={() => setAll((a) => !a)}>{all ? 'Show fewer' : `Show all ${latest.length}`}</Button>}
    </Card>
  );
}

function Messages({ data, onSent }: { data: StatusData; onSent: (m: MessageView) => void }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    setError(null);
    try { onSent(await api.post<MessageView>('/api/intake/messages', { body: body.trim() })); setBody(''); setWriting(false); }
    catch { setError('We couldn’t send your message. Try again.'); }
    finally { setBusy(false); }
  };
  return (
    <>
      {data.messages.length > 0 && (
        <Card>
          <h2 className="card-title mb-2">Messages</h2>
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {data.messages.map((m) => (
              <li key={m.id} className={cx('rounded-xl px-3 py-2 text-sm', m.from === 'staff' ? 'bg-brand-tint' : 'bg-subtle')}>
                <div className="text-[13px] font-semibold">{m.authorName} <span className="font-normal text-muted">· {fmtMonthDay(m.at)}, {fmtTime(m.at)}</span></div>
                <div className="whitespace-pre-wrap [overflow-wrap:anywhere]">{m.body}</div>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Avatar initials={data.lead.initials} tone="soft" />
          <div className="min-w-0">
            <div className="font-semibold">{data.lead.name}</div>
            <div className="text-sm text-muted">{data.lead.title}</div>
            {!writing && (
              <button type="button" className="border-0 bg-transparent p-0 text-sm font-semibold text-brand hover:underline"
                onClick={() => { setWriting(true); requestAnimationFrame(() => box.current?.focus()); }}>Send a message</button>
            )}
          </div>
        </div>
        {writing && (
          <>
            <TextArea ref={box} id="firm-message" small label={`Message ${firstName(data.lead.name)}`} rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
            {error && <div role="alert" className="text-sm font-semibold text-bad">{error}</div>}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => void send()} disabled={busy || !body.trim()}>Send</Button>
              <Button variant="quiet" size="sm" onClick={() => { setWriting(false); setError(null); }}>Cancel</Button>
            </div>
          </>
        )}
      </Card>
    </>
  );
}
